'use client'

import { usePathname, useSelectedLayoutSegment } from 'next/navigation'
import { ReactNode, FC, useEffect, useRef, useState } from 'react'
import Lenis from 'lenis'
import { cx } from '@/src/styles/stylebook'
import { ScrollProvider } from './ScrollContext'

interface SmoothScrollProps {
  children: ReactNode
}

const SmoothScroll: FC<SmoothScrollProps> = ({ children }) => {
  const pathname = usePathname()
  const modalSegment = useSelectedLayoutSegment('modal')
  const lenisRef = useRef<Lenis | null>(null)
  const prevPathRef = useRef(pathname)
  const prevModalSegmentRef = useRef(modalSegment)
  const isFirstRender = useRef(true)
  const scrollUpdateRef = useRef<((y: number) => void) | null>(null)
  const [isMobileDevice, setIsMobileDevice] = useState<boolean | null>(null)

  // 모바일 디바이스 감지 함수
  const detectMobileDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera

    // 모바일 디바이스 User Agent 패턴
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
    const isMobileUA = mobileRegex.test(userAgent)

    // 터치 지원 여부
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0

    // 화면 크기 (모바일 해상도 범위)
    const isMobileSize = window.innerWidth <= 768

    // 모바일 디바이스는: 모바일 UA + 터치 지원 + 작은 화면
    // PC 터치스크린은: 터치 지원 + 큰 화면 (모바일 UA 없음)
    return isMobileUA && hasTouch && isMobileSize
  }

  // Lenis resize 함수
  const resizeLenis = () => {
    if (lenisRef.current && !isMobileDevice) {
      lenisRef.current.resize()
    }
  }

  // 클라이언트 사이드에서만 디바이스 감지
  useEffect(() => {
    setIsMobileDevice(detectMobileDevice())
  }, [])

  useEffect(() => {
    // 디바이스 감지가 완료되지 않았으면 대기
    if (isMobileDevice === null) return

    // 모바일 디바이스면 Lenis 초기화하지 않음
    if (isMobileDevice) {
      return
    }

    // PC 또는 태블릿에서만 Lenis 초기화
    lenisRef.current = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 3,
      touchMultiplier: 4.5,
      infinite: false,
      syncTouch: true,
      syncTouchLerp: 0.1,
      anchors: true,
    })

    lenisRef.current.on('scroll', ({ scroll }: { scroll: number }) => {
      if (scrollUpdateRef.current) {
        scrollUpdateRef.current(scroll)
      }
    })

    function raf(time: number) {
      lenisRef.current?.raf(time)
      requestAnimationFrame(raf)
    }
    requestAnimationFrame(raf)

    return () => {
      lenisRef.current?.destroy()
    }
  }, [isMobileDevice])

  // 동적 콘텐츠 변경 감지 및 Lenis resize 처리
  useEffect(() => {
    if (isMobileDevice || !lenisRef.current) return

    // 1. 이미지 로드 완료 이벤트 리스너
    const handleImageLoad = () => {
      setTimeout(resizeLenis, 100) // 이미지 렌더링 완료 후 resize
    }

    // 2. MutationObserver로 DOM 변경 감지
    const mutationObserver = new MutationObserver(mutations => {
      let shouldResize = false

      mutations.forEach(mutation => {
        // DOM 구조 변경 또는 특정 속성 변경 감지
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldResize = true
        }
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'style' || mutation.attributeName === 'class')
        ) {
          shouldResize = true
        }
      })

      if (shouldResize) {
        setTimeout(resizeLenis, 50)
      }
    })

    // 3. ResizeObserver로 body 크기 변경 감지
    const resizeObserver = new ResizeObserver(() => {
      resizeLenis()
    })

    // 4. 이미지 로드 이벤트 등록
    const images = document.querySelectorAll('img')
    images.forEach(img => {
      if (!img.complete) {
        img.addEventListener('load', handleImageLoad)
        img.addEventListener('error', handleImageLoad) // 에러 시에도 resize
      }
    })

    // DOM 변경 감지 시작
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    })

    // body 크기 변경 감지 시작
    resizeObserver.observe(document.body)

    // 5. 페이지 로드 후 주기적 resize (처음 몇 초간)
    const intervals = [
      setTimeout(resizeLenis, 500),
      setTimeout(resizeLenis, 1000),
      setTimeout(resizeLenis, 2000),
      setTimeout(resizeLenis, 3000),
    ]

    // 6. 전역 이미지 로드 이벤트 (새로 추가되는 이미지들)
    const handleGlobalImageLoad = (e: Event) => {
      const target = e.target as HTMLImageElement
      if (target.tagName === 'IMG') {
        setTimeout(resizeLenis, 100)
      }
    }

    document.addEventListener('load', handleGlobalImageLoad, true)

    // 클리너업
    return () => {
      images.forEach(img => {
        img.removeEventListener('load', handleImageLoad)
        img.removeEventListener('error', handleImageLoad)
      })
      mutationObserver.disconnect()
      resizeObserver.disconnect()
      intervals.forEach(clearTimeout)
      document.removeEventListener('load', handleGlobalImageLoad, true)
    }
  }, [isMobileDevice])

  useEffect(() => {
    // 모바일 디바이스거나 Lenis가 초기화되지 않았으면 스킵
    if (isMobileDevice || !lenisRef.current) return

    const isModalRoute = modalSegment !== null
    const isPathChanged = pathname !== prevPathRef.current
    const isModalSegmentChanged = modalSegment !== prevModalSegmentRef.current

    if (isFirstRender.current) {
      if (window.scrollY !== 0) {
        lenisRef.current?.scrollTo(0, { immediate: true })
      }
      isFirstRender.current = false

      // 첫 렌더링 후 조금 기다렸다가 resize
      setTimeout(resizeLenis, 100)
    } else if (isPathChanged && !isModalRoute) {
      lenisRef.current?.scrollTo(0, { immediate: true })

      // 경로 변경 후 콘텐츠 로드를 위해 resize
      setTimeout(resizeLenis, 100)
      setTimeout(resizeLenis, 500)
    }

    prevPathRef.current = pathname
    prevModalSegmentRef.current = modalSegment
  }, [pathname, modalSegment, isMobileDevice])

  return (
    <ScrollProvider
      onScrollUpdate={updateFn => {
        scrollUpdateRef.current = updateFn
      }}
    >
      <div className={cx('z-0')}>{children}</div>
    </ScrollProvider>
  )
}

export { SmoothScroll }
